import { useState } from "react";
import {
  useGetPendingQuery,
  useGetStatusQuery,
  useApprovePendingMutation,
  useRejectPendingMutation,
  useCancelAllPendingMutation,
  useGetMyPendingQuery,
  useApproveMyPendingMutation,
  useRejectMyPendingMutation,
} from "@/store/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { StatusPill } from "@/components/domain/StatusPill";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { fmtUsd, fmtTime, fmtNum } from "@/lib/format";
import { toast } from "sonner";
import { useAppSelector } from "@/store";

const TABS = ["pending", "executed", "rejected", "failed"] as const;

export default function Pending() {
  const role = useAppSelector((s) => s.auth.user?.role);
  if (role === "admin") return <AdminPending />;
  return <UserPending />;
}

interface PendingRow {
  id: string;
  action: string;
  symbol: string;
  size_usd: number;
  confidence: number | null;
  reasoning?: string | null;
  snapshot_price?: number | null;
  status: string;
  created_at: string;
  error?: string | null;
}

function PendingCardList({
  rows, isLoading, tab, onApprove, onReject, approving, rejecting, priceBySymbol,
}: {
  rows: PendingRow[];
  isLoading: boolean;
  tab: string;
  onApprove: (pid: string) => void;
  onReject: (pid: string) => void;
  approving: boolean;
  rejecting: boolean;
  priceBySymbol?: Map<string, number>;
}) {
  if (isLoading) return <Skeleton className="h-32 w-full" />;
  if (!rows || rows.length === 0) return <p className="text-sm text-muted">No {tab} trades.</p>;
  return (
    <div className="grid gap-3 md:grid-cols-2">
      {rows.map((p) => {
        const current = priceBySymbol?.get(p.symbol);
        const driftPct =
          p.snapshot_price && current ? (current - p.snapshot_price) / p.snapshot_price : null;
        const driftBig = driftPct != null && Math.abs(driftPct) > 0.005;
        return (
          <Card key={p.id} className="bg-bg">
            <CardContent className="space-y-2 p-4">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <Badge tone={p.action === "BUY" ? "good" : "bad"}>{p.action}</Badge>
                  <span className="font-semibold">{p.symbol}</span>
                  <span className="text-muted text-xs">{fmtUsd(p.size_usd)}</span>
                </div>
                <StatusPill status={p.status} />
              </div>
              {p.confidence != null && (
                <div>
                  <div className="text-xs text-muted mb-0.5">
                    Confidence {(p.confidence * 100).toFixed(0)}%
                  </div>
                  <div className="h-1 w-full rounded bg-bg2">
                    <div
                      className="h-1 rounded bg-accent"
                      style={{ width: `${Math.min(100, (p.confidence ?? 0) * 100)}%` }}
                    />
                  </div>
                </div>
              )}
              {p.reasoning && <p className="text-sm text-muted line-clamp-3">{p.reasoning}</p>}
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted">
                {p.snapshot_price != null && <span>snap @ {fmtNum(p.snapshot_price, 4)}</span>}
                {current != null && <span>now @ {fmtNum(current, 4)}</span>}
                {driftPct != null && (
                  <Badge tone={driftBig ? "warn" : "muted"}>
                    drift {(driftPct * 100).toFixed(2)}%
                  </Badge>
                )}
                <span>{fmtTime(p.created_at)}</span>
              </div>
              {p.status === "pending" && (
                <div className="flex gap-2 pt-1">
                  <Button variant="success" size="sm" className="flex-1"
                    disabled={approving} onClick={() => onApprove(p.id)}>
                    Approve & execute
                  </Button>
                  <Button variant="danger" size="sm" className="flex-1"
                    disabled={rejecting} onClick={() => onReject(p.id)}>
                    Reject
                  </Button>
                </div>
              )}
              {p.error && <p className="text-xs text-bad">{p.error}</p>}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

function AdminPending() {
  const [tab, setTab] = useState<(typeof TABS)[number]>("pending");
  const { data, isLoading } = useGetPendingQuery(tab, { pollingInterval: 5000 });
  const status = useGetStatusQuery(undefined, { pollingInterval: 10000 });
  const [approve, { isLoading: approving }] = useApprovePendingMutation();
  const [reject, { isLoading: rejecting }] = useRejectPendingMutation();
  const [cancelAll, { isLoading: cancelling }] = useCancelAllPendingMutation();

  const priceBySymbol = new Map<string, number>(
    (status.data?.markets ?? []).map((m) => [m.symbol, Number(m.price)]),
  );

  const onApprove = async (pid: string) => {
    if (!confirm("Approve and execute this trade?")) return;
    try { await approve(pid).unwrap(); toast.success("Approved + executed"); }
    catch (e) {
      const msg = (e as { data?: { detail?: string } })?.data?.detail ?? "Approve failed";
      toast.error(msg);
    }
  };
  const onReject = async (pid: string) => {
    const reason = prompt("Reject reason (optional)") ?? undefined;
    try { await reject({ pid, reason }).unwrap(); toast.warning("Rejected"); }
    catch { toast.error("Reject failed"); }
  };
  const onCancelAll = async () => {
    if (!confirm("Cancel ALL pending trades?")) return;
    try { await cancelAll().unwrap(); toast.warning("All pending cancelled"); }
    catch { toast.error("Cancel-all failed"); }
  };

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between">
        <CardTitle>Approvals (admin — global)</CardTitle>
        <Button variant="danger" size="sm" disabled={cancelling || tab !== "pending"} onClick={onCancelAll}>
          Cancel all pending
        </Button>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue={tab} onValueChange={(v) => setTab(v as (typeof TABS)[number])}>
          <TabsList>
            {TABS.map((t) => <TabsTrigger key={t} value={t}>{t}</TabsTrigger>)}
          </TabsList>
          {TABS.map((t) => (
            <TabsContent key={t} value={t} className="pt-3">
              <PendingCardList
                rows={(data ?? []) as unknown as PendingRow[]}
                isLoading={isLoading}
                tab={t}
                onApprove={onApprove}
                onReject={onReject}
                approving={approving}
                rejecting={rejecting}
                priceBySymbol={priceBySymbol}
              />
            </TabsContent>
          ))}
        </Tabs>
      </CardContent>
    </Card>
  );
}

function UserPending() {
  const [tab, setTab] = useState<(typeof TABS)[number]>("pending");
  const { data, isLoading } = useGetMyPendingQuery({ status: tab }, { pollingInterval: 5000 });
  const [approve, { isLoading: approving }] = useApproveMyPendingMutation();
  const [reject, { isLoading: rejecting }] = useRejectMyPendingMutation();

  const onApprove = async (pid: string) => {
    if (!confirm("Approve and execute this trade on your account?")) return;
    try { await approve(pid).unwrap(); toast.success("Approved + executed"); }
    catch (e) {
      const msg = (e as { data?: { detail?: string } })?.data?.detail ?? "Approve failed";
      toast.error(msg);
    }
  };
  const onReject = async (pid: string) => {
    const reason = prompt("Reject reason (optional)") ?? undefined;
    try { await reject({ id: pid, reason }).unwrap(); toast.warning("Rejected"); }
    catch { toast.error("Reject failed"); }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Your approvals</CardTitle>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue={tab} onValueChange={(v) => setTab(v as (typeof TABS)[number])}>
          <TabsList>
            {TABS.map((t) => <TabsTrigger key={t} value={t}>{t}</TabsTrigger>)}
          </TabsList>
          {TABS.map((t) => (
            <TabsContent key={t} value={t} className="pt-3">
              <PendingCardList
                rows={(data ?? []) as unknown as PendingRow[]}
                isLoading={isLoading}
                tab={t}
                onApprove={onApprove}
                onReject={onReject}
                approving={approving}
                rejecting={rejecting}
              />
            </TabsContent>
          ))}
        </Tabs>
      </CardContent>
    </Card>
  );
}
